import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
} from '@web/shared/ui';
import { Observable } from 'rxjs';
import { WarehouseProductDataSourceService } from '../../services/warehouse-product-data-source.service';
import { WarehouseProductQuantityHistoryCollectionItem } from '../../models/warehouse-product-quantity-history-collection-item';
import { WarehouseProductQuantityHistoryCollectionFilter } from '../../models/warehouse-product-quantity-history-collection-filter';
import { WarehouseProductQuantityHistoryListItemAction } from '../../enums/warehouse-product-quantity-history-list-item-action';
import { WarehouseProductQuantityHistoryReasonTypeProviderService } from '../../providers/warehouse-product-quantity-history-reason-type-provider.service';
import { Guid } from '@web/utils';
import { WarehouseProductQuantityHistoryListCustomCellTemplateComponent } from '../../components/warehouse-product-quantity-history-list-custom-cell-template/warehouse-product-quantity-history-list-custom-cell-template.component';

@Component({
  selector: 'app-warehouse-product-quantity-history-list',
  imports: [
    TableComponent,
    WarehouseProductQuantityHistoryListCustomCellTemplateComponent,
  ],
  templateUrl: './warehouse-product-quantity-history-list.component.html',
  styleUrl: './warehouse-product-quantity-history-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseProductQuantityHistoryListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseProductQuantityHistoryCollectionItem, WarehouseProductQuantityHistoryCollectionFilter, WarehouseProductQuantityHistoryListItemAction>).create();
  /* prettier-ignore */
  private readonly reasonTypeProvider = inject(WarehouseProductQuantityHistoryReasonTypeProviderService);
  /* prettier-ignore */
  private readonly warehouseProductDataSource = inject(WarehouseProductDataSourceService);

  public readonly owner = input.required<string>();
  public readonly id = input.required<Guid>();

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseProductQuantityHistoryCollectionItem,
    WarehouseProductQuantityHistoryCollectionFilter,
    WarehouseProductQuantityHistoryListItemAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder
          .initCustom('change')
          .width('12rem')
          .filterKey('change')
          .sortKey('change')
          .label('Ilość')
          .build(),

        builder
          .initDateTime('createdAt')
          .width('12rem')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .label('Data zmiany')
          .build(),

        builder
          .initText('createdBy')
          .width('12rem')
          .filterKey('createdBy')
          .sortKey('createdBy')
          .label('Autor')
          .build(),

        builder
          .initCustom('reason')
          .width('18rem')
          .filterKey('reason')
          .sortKey('reason')
          .label('Powód zmiany')
          .build(),

        builder
          .initCustom('receipt')
          .width('12rem')
          .label('Przyjęcie')
          .filterKey('receipt')
          .sortKey('receipt')
          .build(),

        builder
          .initCustom('restock')
          .width('12rem')
          .label('Zwrot')
          .filterKey('restock')
          .sortKey('restock')
          .build(),

        builder
          .initCustom('release')
          .width('12rem')
          .label('Wydanie')
          .filterKey('release')
          .sortKey('release')
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initNumber('change')
          .build(),

        builder //
          .initSelect('reason')
          .dictionary(() => this.reasonTypeProvider.selectItems())
          .build(),

        builder //
          .initText('receipt')
          .build(),

        builder //
          .initText('restock')
          .build(),

        builder //
          .initText('release')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder //
          .initText('createdBy')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<
          TableDataPayload<WarehouseProductQuantityHistoryCollectionItem>
        > =>
          this.warehouseProductDataSource.fetchQuantityHistoryCollection({
            lazyLoad,
            owner: this.owner(),
            id: this.id(),
          }),
      })
      .build();
  }
}
